import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Edit, Loader2, Calendar, Search, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { usePagination, PaginationBar } from "@/components/PaginationBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewsManagement({ userRole }) {
  const [notizie, setNotizie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ titolo: "", contenuto: "" });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);

  const fetchNotizie = async () => {
    try { const { data } = await axios.get(`${API}/notizie`); setNotizie(data); }
    catch { toast.error("Errore caricamento notizie"); }
    setLoading(false);
  };
  useEffect(() => { fetchNotizie(); }, []);

  const openCreate = () => { setEditing(null); setForm({ titolo: "", contenuto: "" }); setShowForm(true); };
  const openEdit = (n) => { setEditing(n); setForm({ titolo: n.titolo, contenuto: n.contenuto }); setDetail(null); setShowForm(true); };

  const handleSave = async () => {
    if (!form.titolo || !form.contenuto) { toast.error("Compila titolo e contenuto"); return; }
    setSaving(true);
    try {
      if (editing) { await axios.put(`${API}/notizie/${editing.id}`, form); toast.success("Notizia aggiornata"); }
      else { await axios.post(`${API}/notizie`, form); toast.success("Notizia pubblicata"); }
      setShowForm(false); fetchNotizie();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questa notizia?")) return;
    try { await axios.delete(`${API}/notizie/${id}`); toast.success("Notizia eliminata"); fetchNotizie(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const searched = notizie.filter(n => `${n.titolo} ${n.contenuto} ${n.autore}`.toLowerCase().includes(search.toLowerCase()));
  const pg = usePagination(searched);

  if (loading) return <div className="text-center py-12 text-white/30">Caricamento...</div>;

  if (detail) return (
    <div data-testid="news-detail">
      <button onClick={() => setDetail(null)} className="flex items-center gap-1.5 text-sm text-white/40 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Torna</button>
      <div className="bg-[#13243d] border border-[#1e3456] rounded-xl p-6 shadow-lg shadow-black/20">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>{detail.titolo}</h2>
          <Button size="sm" onClick={() => openEdit(detail)} className="bg-[#1a3a5c] text-[#5ba8d9] hover:bg-[#2a5080] border border-[#2a5080] text-xs" data-testid="edit-news-from-detail"><Edit className="w-3 h-3 mr-1" /> Modifica</Button>
        </div>
        <p className="text-sm text-white/60 leading-relaxed whitespace-pre-wrap bg-[#0f1d32] rounded-lg p-4 border border-[#1e3456]">{detail.contenuto}</p>
        <div className="flex items-center gap-4 mt-4 text-xs text-white/25">
          <span>di {detail.autore}</span>
          <span>{new Date(detail.created_at).toLocaleString('it-IT')}</span>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="news-management">
      <div className="flex items-center justify-between mb-6">
        <div className="relative flex-1 max-w-sm"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" /><Input placeholder="Cerca notizie..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/20" data-testid="search-news" /></div>
        <Button data-testid="create-news-btn" onClick={openCreate} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90 font-medium"><Plus className="w-4 h-4 mr-2" /> Nuova Notizia</Button>
      </div>
      <div className="space-y-3">
        {notizie.filter(n => `${n.titolo} ${n.contenuto} ${n.autore}`.toLowerCase().includes(search.toLowerCase())).map(n => (
          <div key={n.id} className="glass-card rounded-lg border border-white/8 p-5 cursor-pointer hover:border-[#00A0D1]/20 transition-all" onClick={() => setDetail(n)} data-testid={`news-card-${n.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-base font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{n.titolo}</h3>
                <p className="text-sm text-white/40 mt-2 line-clamp-2">{n.contenuto}</p>
                <div className="flex items-center gap-4 mt-3">
                  <p className="text-xs text-white/25">di {n.autore}</p>
                  <div className="flex items-center gap-1 text-xs text-white/25"><Calendar className="w-3 h-3" /> {new Date(n.created_at).toLocaleDateString('it-IT')}</div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(n); }} className="text-white/30 hover:text-[#00A0D1] hover:bg-white/5" data-testid={`edit-news-${n.id}`}><Edit className="w-3.5 h-3.5" /></Button>
                {userRole === "admin" && <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDelete(n.id); }} className="text-white/30 hover:text-red-400 hover:bg-red-500/5" data-testid={`delete-news-${n.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
              </div>
            </div>
          </div>
        ))}
        {notizie.length === 0 && <div className="text-center py-12 text-white/30">Nessuna notizia pubblicata</div>}
      </div>
      <PaginationBar {...pg} />
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg bg-[#0d1b2e] border-white/10 text-white" data-testid="news-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{editing ? "Modifica Notizia" : "Nuova Notizia"}</DialogTitle>
            <DialogDescription className="text-white/40">{editing ? "Modifica la notizia" : "Pubblica una nuova notizia"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs text-white/60">Titolo *</Label><Input data-testid="news-titolo" value={form.titolo} onChange={e => setForm(p => ({ ...p, titolo: e.target.value }))} className="mt-1 bg-white/5 border-white/10 text-white" /></div>
            <div><Label className="text-xs text-white/60">Contenuto *</Label><Textarea data-testid="news-contenuto" value={form.contenuto} onChange={e => setForm(p => ({ ...p, contenuto: e.target.value }))} className="mt-1 bg-white/5 border-white/10 text-white" rows={6} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} className="border-white/10 text-white/50 bg-transparent hover:bg-white/5">Annulla</Button>
            <Button data-testid="submit-news" onClick={handleSave} disabled={saving} className="bg-[#C9A84C] text-[#001830] hover:bg-[#C9A84C]/90">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editing ? "Salva" : "Pubblica")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
